/**
 * LeadEditorPage — full-page create/edit view for a sales lead.
 *
 * Routes:
 *   /sales-pipeline/new          → create mode
 *   /sales-pipeline/leads/:id    → edit mode
 *
 * Replaces the previous LeadDialog. Same fields, same API contract,
 * but the extra room lets us keep long referrer / notes blocks
 * comfortable without the modal squeeze.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Save, Trash2, TrendingUp, Gift, Building2,
  User, DollarSign, Calendar, Activity,
} from "lucide-react";

const STAGES = [
  { key: "cold", label: "Cold" },
  { key: "warm", label: "Warm" },
  { key: "hot", label: "Hot" },
  { key: "qualified", label: "Qualified" },
  { key: "won", label: "Won" },
  { key: "lost", label: "Lost" },
];

const SOURCES = [
  "Referral", "Website", "Cold call", "Email outreach", "LinkedIn",
  "Networking event", "Trade show", "Existing client", "Google Ads",
  "SEO / organic", "Other",
];

const emptyForm = {
  company_name: "",
  contact_first_name: "", contact_last_name: "",
  contact_email: "", contact_phone: "",
  website: "", source: "", stage: "cold",
  estimated_value: "", next_action: "", next_action_date: "", notes: "",
  referred_by_first_name: "", referred_by_last_name: "",
  referred_by_email: "", referred_by_phone: "",
  referred_by_company: "", referred_by_notes: "",
};

const fmtActivityTs = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", {
      day: "numeric", month: "short", year: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function LeadEditorPage() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const isNew = !leadId;

  const [lead, setLead] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/sales-pipeline/leads/${leadId}`);
      setLead(data);
      setForm({
        company_name: data.company_name || "",
        contact_first_name: data.contact_first_name || "",
        contact_last_name: data.contact_last_name || "",
        contact_email: data.contact_email || "",
        contact_phone: data.contact_phone || "",
        website: data.website || "",
        source: data.source || "",
        stage: data.stage || "cold",
        estimated_value: data.estimated_value ?? "",
        next_action: data.next_action || "",
        next_action_date: data.next_action_date || "",
        notes: data.notes || "",
        referred_by_first_name: data.referred_by_first_name || "",
        referred_by_last_name: data.referred_by_last_name || "",
        referred_by_email: data.referred_by_email || "",
        referred_by_phone: data.referred_by_phone || "",
        referred_by_company: data.referred_by_company || "",
        referred_by_notes: data.referred_by_notes || "",
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load lead");
      navigate("/sales-pipeline");
    } finally { setLoading(false); }
  }, [leadId, isNew, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.company_name?.trim()) {
      toast.error("Organisation name is required"); return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        estimated_value: form.estimated_value === "" ? null
          : Number(form.estimated_value),
      };
      if (!payload.contact_email) delete payload.contact_email;
      if (!payload.referred_by_email) delete payload.referred_by_email;
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "") payload[k] = null;
      });
      if (isNew) {
        const { data } = await api.post("/sales-pipeline/leads", payload);
        toast.success("Lead created");
        navigate(`/sales-pipeline/leads/${data.lead_id}`, { replace: true });
      } else {
        await api.put(`/sales-pipeline/leads/${leadId}`, payload);
        toast.success("Lead saved");
        await load();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(
      `Delete lead "${lead?.company_name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/sales-pipeline/leads/${leadId}`);
      toast.success("Lead deleted");
      navigate("/sales-pipeline");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin inline text-zinc-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-5xl" data-testid="lead-editor-page">
      {/* Header */}
      <div className="flex items-start gap-3 flex-wrap">
        <Button variant="outline" size="sm" className="rounded-sm"
          onClick={() => navigate("/sales-pipeline")}
          data-testid="lead-editor-back">
          <ArrowLeft className="h-4 w-4 mr-1" /> Sales Pipeline
        </Button>
        <div className="h-12 w-12 grid place-items-center rounded-sm bg-gradient-to-br from-orange-500 to-red-600 text-white shrink-0">
          <TrendingUp className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-[240px]">
          <div className="label-overline">Sales Pipeline</div>
          <h1 className="font-display font-extrabold text-3xl tracking-tight">
            {isNew ? "New lead" : (lead?.company_name || "Edit lead")}
          </h1>
          {!isNew && lead && (
            <div className="text-xs text-zinc-500 mt-1">
              Stage: <span className="font-semibold">
                {STAGES.find((s) => s.key === lead.stage)?.label || lead.stage}
              </span>
              {" · "}Updated {fmtActivityTs(lead.updated_at)}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <Button variant="outline" size="sm" className="rounded-sm text-red-600 hover:text-red-700"
              onClick={remove} data-testid="lead-editor-delete">
              <Trash2 className="h-4 w-4 mr-1.5" /> Delete
            </Button>
          )}
          <Button size="sm" className="rounded-sm bg-[#002FA7] hover:bg-[#001f70] text-white"
            onClick={save} disabled={busy}
            data-testid="lead-editor-save">
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
              : <Save className="h-4 w-4 mr-1.5" />}
            {isNew ? "Create lead" : "Save changes"}
          </Button>
        </div>
      </div>

      {/* Organisation + contact */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-zinc-500" />
            <div className="font-display font-bold text-lg">Organisation &amp; contact</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">Organisation *</Label>
              <Input value={form.company_name} autoFocus={isNew}
                onChange={(e) => set("company_name", e.target.value)}
                className="rounded-sm" data-testid="lead-company-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Contact first name</Label>
              <Input value={form.contact_first_name}
                onChange={(e) => set("contact_first_name", e.target.value)}
                className="rounded-sm" data-testid="lead-contact-first-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Contact last name</Label>
              <Input value={form.contact_last_name}
                onChange={(e) => set("contact_last_name", e.target.value)}
                className="rounded-sm" data-testid="lead-contact-last-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Website</Label>
              <Input value={form.website}
                onChange={(e) => set("website", e.target.value)}
                className="rounded-sm" placeholder="https://…"
                data-testid="lead-website-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Contact email</Label>
              <Input type="email" value={form.contact_email}
                onChange={(e) => set("contact_email", e.target.value)}
                className="rounded-sm" data-testid="lead-contact-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Contact phone</Label>
              <Input value={form.contact_phone}
                onChange={(e) => set("contact_phone", e.target.value)}
                className="rounded-sm" data-testid="lead-contact-phone-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Sales details */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-zinc-500" />
            <div className="font-display font-bold text-lg">Sales details</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="label-overline">Stage</Label>
              <Select value={form.stage} onValueChange={(v) => set("stage", v)}>
                <SelectTrigger className="rounded-sm" data-testid="lead-stage-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STAGES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Source</Label>
              <Select value={form.source || "__none__"}
                onValueChange={(v) => set("source", v === "__none__" ? "" : v)}>
                <SelectTrigger className="rounded-sm" data-testid="lead-source-select">
                  <SelectValue placeholder="—" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">—</SelectItem>
                  {SOURCES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Estimated value (AUD)</Label>
              <Input type="number" min="0" step="100"
                value={form.estimated_value}
                onChange={(e) => set("estimated_value", e.target.value)}
                className="rounded-sm" data-testid="lead-value-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Next action date</Label>
              <Input type="date" value={form.next_action_date}
                onChange={(e) => set("next_action_date", e.target.value)}
                className="rounded-sm" data-testid="lead-next-action-date" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">Next action</Label>
              <Input value={form.next_action}
                onChange={(e) => set("next_action", e.target.value)}
                className="rounded-sm"
                placeholder="e.g. Follow up on quote, Send SEO audit…"
                data-testid="lead-next-action-input" />
            </div>
            <div className="md:col-span-3 space-y-1.5">
              <Label className="label-overline">Notes</Label>
              <Textarea rows={5} value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                className="rounded-sm resize-y min-h-[120px]"
                placeholder="Meeting notes, requirements, decisions, next steps…"
                data-testid="lead-notes-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Referred by */}
      <Card className="rounded-sm border-zinc-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Gift className="h-4 w-4 text-fuchsia-600" />
            <div className="font-display font-bold text-lg">Referred by</div>
            <span className="text-[11px] text-zinc-400 ml-1">
              Optional · used by the future referral rewards programme
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="label-overline">Referrer first name</Label>
              <Input value={form.referred_by_first_name}
                onChange={(e) => set("referred_by_first_name", e.target.value)}
                className="rounded-sm" data-testid="lead-referred-by-first-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Referrer last name</Label>
              <Input value={form.referred_by_last_name}
                onChange={(e) => set("referred_by_last_name", e.target.value)}
                className="rounded-sm" data-testid="lead-referred-by-last-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Referrer organisation</Label>
              <Input value={form.referred_by_company}
                onChange={(e) => set("referred_by_company", e.target.value)}
                className="rounded-sm" data-testid="lead-referred-by-company-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-overline">Referrer phone</Label>
              <Input value={form.referred_by_phone}
                onChange={(e) => set("referred_by_phone", e.target.value)}
                className="rounded-sm" data-testid="lead-referred-by-phone-input" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">Referrer email</Label>
              <Input type="email" value={form.referred_by_email}
                onChange={(e) => set("referred_by_email", e.target.value)}
                className="rounded-sm" data-testid="lead-referred-by-email-input" />
            </div>
            <div className="md:col-span-2 space-y-1.5">
              <Label className="label-overline">Referrer notes</Label>
              <Input value={form.referred_by_notes}
                onChange={(e) => set("referred_by_notes", e.target.value)}
                className="rounded-sm"
                placeholder="e.g. Reward code, relationship, promised incentive…"
                data-testid="lead-referred-by-notes-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activity trail */}
      {!isNew && (lead?.activity || []).length > 0 && (
        <Card className="rounded-sm border-zinc-200" data-testid="lead-activity-card">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2">
              <Activity className="h-4 w-4 text-zinc-500" />
              <div className="font-display font-bold text-lg">Activity</div>
              <span className="text-[11px] text-zinc-400 ml-1">
                {(lead.activity || []).length} events
              </span>
            </div>
            <ul className="space-y-2">
              {[...(lead.activity || [])].reverse().slice(0, 25).map((a, i) => (
                <li key={`${a.ts}-${i}`}
                  className="text-xs flex gap-2 items-start pb-2 border-b border-zinc-100 last:border-b-0">
                  <User className="h-3 w-3 text-zinc-300 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-700">{a.note || a.type}</div>
                    <div className="text-[10px] text-zinc-400 mt-0.5 flex items-center gap-1">
                      <Calendar className="h-2.5 w-2.5" />
                      {fmtActivityTs(a.ts)}
                      {a.by && <span>· {a.by}</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
